import EmblaCarousel from 'embla-carousel';

class Slider {
  #sliderContainer;
  #tweenNodes = [];
  #emblaViewportNode = null;
  #emblaContainerNode = null;
  #dotNodes = [];
  #prevButton = null;
  #nextButton = null;

  constructor(sliderContainer) {
    this.#sliderContainer = sliderContainer;
  }

  getTweenNodes(emblaSlider) {
    this.#tweenNodes = emblaSlider.slideNodes().map(slideNode => {
      return slideNode.querySelector('.ec-slider__embla__slide__inner');
    });
  }

  setEmblaViewportContainerMinHeight() {
    this.#emblaViewportNode.style.minHeight = '';
    this.#emblaContainerNode.style.minHeight = '';
    const currentEmblarViewportHeight = this.#emblaViewportNode.getBoundingClientRect().height;
    this.#emblaViewportNode.style.minHeight = `${currentEmblarViewportHeight}px`;
    this.#emblaContainerNode.style.minHeight = `${currentEmblarViewportHeight}px`;
  }

  tweenScale(emblaSlider, evt) {
    const emblaNode = this.#sliderContainer.querySelector('.ec-slider__embla');
    const emblaNodeWidth = emblaNode.getBoundingClientRect().width;
    const overlapResponsiveExtend = window.innerWidth >= 1280 && emblaNodeWidth < 625 ? (625 - emblaNodeWidth) / 2 : 0;
    const overlapBase = window.innerWidth >= 1280 ? .67 : .4;
    const widthBase = window.innerWidth >= 1280 ? 360 : 300;
    const secondOuterRingsWidthBase = widthBase * .8;
    const outerRingsWidthBase = secondOuterRingsWidthBase * .8;
    const minHeightBase = window.innerWidth >= 1280 ? 300 : 250;
    const secondRingMinHeightBase = minHeightBase * .8;
    const outerRingsMinHeightBase = secondRingMinHeightBase * .8;
    const internalEngine = emblaSlider.internalEngine();
    const scrollProgress = emblaSlider.scrollProgress();

    emblaSlider.snapList().forEach((scrollSnap, snapIndex) => {
      let targetDifference = scrollSnap - scrollProgress;
      const slidesInSnap = internalEngine.scrollSnapList.slidesBySnap[snapIndex];

      slidesInSnap.forEach(slideIndex => {
        if (internalEngine.options.loop) {
          internalEngine.slideLooper.loopPoints.forEach(loopItem => {
            const targetItem = loopItem.target();

            if (slideIndex === loopItem.index && targetItem !== 0) {
              const mathSign = Math.sign(targetItem);

              if (mathSign === -1) {
                targetDifference = scrollSnap - (1 + scrollProgress);
              }

              if (mathSign === 1) {
                targetDifference = scrollSnap + (1 - scrollProgress);
              }
            }
          });
        }

        const snapList = emblaSlider.snapList();
        const snapDistance = Math.abs(snapList[1] - snapList[0]);
        const rawDistance = Math.abs(targetDifference);
        const normalized = Math.min(Math.max((rawDistance / snapDistance), 0), 1);
        const tweenValue = 1 - normalized;
        const direction = Math.sign(targetDifference);
        const isNextOrPrev = parseFloat(rawDistance.toFixed(5)) <= parseFloat((1 / this.#tweenNodes.length).toFixed(5));

        let minHeight;

        if (isNextOrPrev) {
          minHeight = secondRingMinHeightBase + tweenValue * (minHeightBase - secondRingMinHeightBase);
        } else {
          const outerTween = Math.min(1, ((1 / this.#tweenNodes.length) / rawDistance));
          minHeight = outerRingsMinHeightBase + outerTween * (secondRingMinHeightBase - outerRingsMinHeightBase);
        }

        minHeight = Math.max(outerRingsMinHeightBase, Math.min(minHeightBase, minHeight));

        const width = isNextOrPrev ? secondOuterRingsWidthBase + tweenValue * (widthBase - secondOuterRingsWidthBase) : outerRingsWidthBase + (Math.min(1, ((1 / this.#tweenNodes.length) / rawDistance))) * (secondOuterRingsWidthBase - outerRingsWidthBase);
        const opacity = .5 + tweenValue * (1 - .5);
        const overlapPx = (width * overlapBase) + overlapResponsiveExtend;
        const shift = rawDistance * overlapPx * direction * -1 * this.#tweenNodes.length;
        const maxShift = this.#tweenNodes.length * overlapPx;
        const clampedShift = Math.max(Math.min(shift, maxShift), -maxShift);

        const activeSnapIndex = rawDistance < (1 / this.#tweenNodes.length * overlapBase);

        let zIndex;

        if (activeSnapIndex) {
          zIndex = 10;
        } else {
          if (direction < 0) {
            zIndex = 9;
          } else {
            zIndex = Math.max(10 - snapIndex, 1);
          }
        }

        const tweenNode = this.#tweenNodes[slideIndex];
        tweenNode.style.minHeight = `${minHeight}px`;
        tweenNode.style.transform = `translateX(${(clampedShift + (direction < 0 ? -1 : 1))}px) scale(${(width / widthBase)})`;
        tweenNode.querySelector('.ec-slider__embla__slide__content').style.opacity = opacity;

        const tweenParent = tweenNode.parentElement;
        tweenParent.style.zIndex = `${zIndex}`;
      });
    });

    if (typeof evt === 'undefined' || typeof evt !== 'undefined' && evt.type === 'reinit') {
      this.setEmblaViewportContainerMinHeight();
    }

    this.toggleActiveSlide(emblaSlider);
  }

  tweenEmblaSlider(emblaSlider) {
    this.getTweenNodes(emblaSlider);
    this.tweenScale(emblaSlider);

    emblaSlider.on('reinit', this.getTweenNodes.bind(this));
    emblaSlider.on('reinit', this.tweenScale.bind(this));
    emblaSlider.on('scroll', this.tweenScale.bind(this));
    emblaSlider.on('slideFocus', this.tweenScale.bind(this));

    window.addEventListener('resize', emblaSlider.reInit);
  }

  toggleActiveDot(emblaSlider) {
    const previous = emblaSlider.previousSnap();
    const selected = emblaSlider.selectedSnap();
    this.#dotNodes[previous].setAttribute('aria-current', 'false');
    this.#dotNodes[selected].setAttribute('aria-current', 'true');
  }

  toggleActiveSlide(emblaSlider) {
    const selected = emblaSlider.selectedSnap();
    const selectedNode = this.#tweenNodes[selected];

    this.#tweenNodes.forEach((slideNode, index) => {
      if (index !== selected) {
        slideNode.parentElement.setAttribute('aria-hidden', 'true');
        const focusableElemeentsSlideNode = slideNode.querySelectorAll('a, button');

        focusableElemeentsSlideNode.forEach(focusableElemeent => {
          focusableElemeent.setAttribute('tabindex', '-1');
        });
      }
    });

    selectedNode.parentElement.removeAttribute('aria-hidden');
    const focusableElemeentsSelectedNode = selectedNode.querySelectorAll('a, button');

    focusableElemeentsSelectedNode.forEach(focusableElemeent => {
      focusableElemeent.removeAttribute('tabindex');
    });
  }

  scrollToSlide(emblaSlider, index) {
    emblaSlider.goTo(index);
  }

  setDotListeners(emblaSlider) {
    this.#dotNodes.forEach((dotNode, dotIndex) => {
      dotNode.addEventListener('click', () => {
        this.scrollToSlide(emblaSlider, dotIndex);
      });
    });

    emblaSlider.on('reinit', this.toggleActiveDot.bind(this));
    emblaSlider.on('select', this.toggleActiveDot.bind(this));
  }

  setArrowButtonsListeners(emblaSlider) {
    this.#prevButton.addEventListener('clickEvent', () => {
      emblaSlider.goToPrev();
    });

    this.#nextButton.addEventListener('clickEvent', () => {
      emblaSlider.goToNext();
    });
  }

  init() {
    const sliderOptions = {
      loop: true,
      duration: 30
    };

    const emblaNode = this.#sliderContainer.querySelector('.ec-slider__embla');
    this.#emblaContainerNode = emblaNode.querySelector('.ec-slider__embla__container');
    this.#emblaViewportNode = emblaNode.querySelector('.ec-slider__embla__viewport');
    this.#dotNodes = this.#sliderContainer.querySelectorAll('.ec-slider__embla__dot');
    this.#prevButton = this.#sliderContainer.querySelector('.ec-slider__embla__arrow-controls__prev');
    this.#nextButton = this.#sliderContainer.querySelector('.ec-slider__embla__arrow-controls__next');

    const emblaSlider = EmblaCarousel(this.#emblaViewportNode, sliderOptions);
    this.tweenEmblaSlider(emblaSlider);
    this.setDotListeners(emblaSlider);
    this.setArrowButtonsListeners(emblaSlider);
  }
}

const sliderElements = document.querySelectorAll('[data-js-block="slider"]');

if (sliderElements.length > 0) {
  sliderElements.forEach(sliderElement => {
    const sliderElementClass = new Slider(sliderElement);
    sliderElementClass.init();
  });
}

export default Slider;
